import * as THREE from 'three';
import { params } from '../config.js';
import * as SceneModule from '../scene.js';
import { mouseControl, airplaneState } from '../airplane/state.js';
import { triggerRadarPulse } from '../effects/radar.js';

// Mouse NDC for hover effects (exported for use in main.js)
export const mouseNdc = new THREE.Vector2(-2, -2);

// Right-click drag for camera orbit around airplane
export const cameraOrbit = {
  isDragging: false,
  lastX: 0,
  lastY: 0,
  azimuth: 0,       // horizontal angle (radians)
  elevation: 0,     // vertical angle offset (radians) - for pitch adjustment
  sensitivity: 0.005,
  // Min/max elevation for each mode (radians)
  elevationLimits: {
    chase: { min: -0.3, max: 0.5 },
    topdown: { min: -0.2, max: 0.2 },  // limited for top-down
    isometric: { min: -0.4, max: 0.6 }
  },
  // Easing for returning to default position
  easeSpeed: 3.0  // how fast to ease back (radians per second equivalent)
};

// Check if camera orbit is allowed (only when landed)
function canOrbitCamera() {
  return airplaneState.flightState === 'landed';
}

// Touch tap detection
let touchStartTime = 0;
let touchStartPos = { x: 0, y: 0 };
const TAP_THRESHOLD_MS = 300;  // Max time for a tap
const TAP_THRESHOLD_PX = 20;   // Max movement for a tap

export function setupMouseControls() {
  const { canvasContainer } = SceneModule;
  // Mouse tracking for hover effects and airplane direction control
  canvasContainer.addEventListener('mousemove', (e) => {
    const rect = canvasContainer.getBoundingClientRect();
    mouseNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Update airplane direction based on mouse position
    // X position controls turn rate (-1 to 1, continuous turning)
    // Y position controls climb rate (-1 to 1, continuous altitude change)
    mouseControl.active = true;
    mouseControl.turnRate = mouseNdc.x;   // -1 (left) to 1 (right)
    mouseControl.climbRate = mouseNdc.y;  // -1 (down) to 1 (up)
  });

  canvasContainer.addEventListener('mouseleave', () => {
    mouseNdc.set(-2, -2);
    mouseControl.active = false;
  });

  // Right-click drag for camera orbit (only when landed)
  canvasContainer.addEventListener('mousedown', (e) => {
    if (e.button === 2) { // Right click
      if (canOrbitCamera()) {
        cameraOrbit.isDragging = true;
        cameraOrbit.lastX = e.clientX;
        cameraOrbit.lastY = e.clientY;
      }
      e.preventDefault();
    }
  });

  window.addEventListener('mousemove', (e) => {
    if (!cameraOrbit.isDragging) return;
    
    // Stop dragging if no longer allowed (e.g., launched while dragging)
    if (!canOrbitCamera()) {
      cameraOrbit.isDragging = false;
      return;
    }
    
    const deltaX = e.clientX - cameraOrbit.lastX;
    const deltaY = e.clientY - cameraOrbit.lastY;
    cameraOrbit.lastX = e.clientX;
    cameraOrbit.lastY = e.clientY;
    
    // Update azimuth (horizontal orbit)
    cameraOrbit.azimuth -= deltaX * cameraOrbit.sensitivity;
    
    // Update elevation (vertical angle) with limits based on mode
    const mode = params?.cameraMode || 'chase';
    const limits = cameraOrbit.elevationLimits[mode] || { min: -0.5, max: 0.5 };
    cameraOrbit.elevation = Math.max(limits.min, Math.min(limits.max, 
      cameraOrbit.elevation + deltaY * cameraOrbit.sensitivity
    ));
  });

  window.addEventListener('mouseup', (e) => {
    if (e.button === 2) {
      cameraOrbit.isDragging = false;
    }
  });

  // Prevent context menu on right-click
  canvasContainer.addEventListener('contextmenu', (e) => {
    e.preventDefault();
  });

  // Touch support for mobile: tap to trigger radar pulse
  canvasContainer.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      touchStartTime = performance.now();
      touchStartPos.x = e.touches[0].clientX;
      touchStartPos.y = e.touches[0].clientY;
    }
  }, { passive: true });

  canvasContainer.addEventListener('touchend', (e) => {
    const touchDuration = performance.now() - touchStartTime;
    
    // Check if this was a quick tap (not a drag or long press)
    if (touchDuration < TAP_THRESHOLD_MS && e.changedTouches.length === 1) {
      const dx = e.changedTouches[0].clientX - touchStartPos.x;
      const dy = e.changedTouches[0].clientY - touchStartPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // If finger didn't move much, treat as a tap and trigger radar
      if (distance < TAP_THRESHOLD_PX) {
        triggerRadarPulse();
      }
    }
  }, { passive: true });
}
